import React from 'react'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import { PAGE_PATH } from 'constants/pagePath'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import { DestinationTypes } from 'states/QnState'
import { useAddressMatch, useEditQnState } from 'utils/commonHooks'
import { OptionItem } from '../common'
import ConfirmGroup from '../form/ConfirmGroup'
import RadioButtonGroup from '../form/RadioButtonGroup'
import SelectLinkGroup from '../form/SelectLinkGroup'
import SwitchButtonGroup from '../form/SwitchButtonGroup'
import DestinationIcon from '../icons/DestinationIcon'
import MapConfirmContent from './confirmContents/MapConfirmContent'
import ErrorMessage from './ErrorMessage'

/** 搬送先タイプとタイプごとの詳細を選択するコンポーネント */
export default function SelectDestinationContent(): JSX.Element {
  const [editState, , setVehicleStateInfo] = useEditQnState()

  // 搬送先タイプの選択肢
  const destinationTypeOptions = React.useMemo(
    () =>
      Object.entries(CLIENT_SETTINGS.options.destinationType).map(
        ([k, v]) =>
          ({ label: v as DestinationTypes, value: k } as OptionItem<
            DestinationTypes
          >)
      ),
    [CLIENT_SETTINGS]
  )

  // 搬送先タイプの変更イベントハンドラ
  const handleDestinationTypeChanged = React.useCallback(
    (value: DestinationTypes) => {
      setVehicleStateInfo({
        destinationType: value,
      })
    },
    [editState]
  )

  // 都道府県外移動の変更イベントハンドラ
  const handleCrossPrefecturesChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        crossPrefectures: value,
      })
    },
    [editState]
  )

  const addressMatchError = useAddressMatch(editState)

  return (
    <>
      <RadioButtonGroup
        title={`${VEHICLE_STATE_FORM.destinationType.formName}の選択`}
        isRequired={true}
        options={destinationTypeOptions}
        selectedValue={editState.vehicleStateInfo.destinationType}
        onValueChanged={handleDestinationTypeChanged}
      />
      {editState.vehicleStateInfo.destinationType ===
      DestinationTypes.specify ? (
        editState.destinationLocation.isSelected ? (
          // ご存知のディーラー・修理工場など予定 選択中
          // 運び先入力済み
          <>
            <ConfirmGroup
              title={'運び先の場所を指定'}
              titleLabel={'運び先'}
              isRequired
              content={
                <MapConfirmContent
                  component={<DestinationIcon />}
                  {...editState.destinationLocation}
                  boldLocationText
                />
              }
              changeLinkTo={PAGE_PATH.TRANSPORT_DESTINATION}
            />
            {addressMatchError && (
              <ErrorMessage
                message={
                  'トラブル場所と運び先が同一です。\n内容のご確認をお願いいたします。'
                }
                justifyContent="left"
              />
            )}
          </>
        ) : (
          // ご存知のディーラー・修理工場など予定 選択中
          // 運び先入力前
          <SelectLinkGroup
            title={'運び先の場所を指定'}
            isRequired
            forwardTo={PAGE_PATH.TRANSPORT_DESTINATION}
            lineLabel={'運び先'}
            arrowLabel={'選択する'}
          />
        )
      ) : (
        // スタッフ到着までに検討しておく 選択中
        editState.vehicleStateInfo.destinationType ===
          DestinationTypes.consider && (
          <SwitchButtonGroup
            title={`${VEHICLE_STATE_FORM.crossPrefectures.formName}を選択`}
            isSwitched={editState.vehicleStateInfo.crossPrefectures}
            label={VEHICLE_STATE_FORM.crossPrefectures.label}
            message={
              '現在地とは異なる都道府県に運ぶご希望はございますか？（ご自宅が遠方など）'
            }
            onChanged={handleCrossPrefecturesChanged}
          />
        )
      )}
    </>
  )
}
